import { Box, Typography, Button, LinearProgress, Grid2 } from '@mui/material';
import ZipFileUpload from './components/zipFileUpload';
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import Resumable from 'resumablejs';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer, Slide } from 'react-toastify';
import byteConversion from './components/byteConversion';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"

export default function DatasetSubmission({isAdmin, user, isSubmit}) {
    const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB
    const MAX_CHUNK_SIZE_BYTES = 1 * 1024 * 1024; // 1MB chunks
    const serverHost = process.env.REACT_APP_BACKEND_SERVER_HOST || 'https://localhost:3001';
    const {email} = user !== undefined ? user : {};

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [allowed, setAllowed] = useState(true);
    const [bannerShow, setBannerShow] = useState(false);
    const [uploadType, setUploadType] = useState('');
    const [fileName, setFileName] = useState('');

    const navigate = useNavigate();
    const toastOptions = {
        position: "bottom-center",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Slide
    }

    // maps upload file keys to file objects
    const [files, setFiles] = useState({
        datasetZip: null,
    });

    const fetchSubmissions = useCallback(async () => {
        try {
          const response = await axios.get(`${serverHost}/submissions/${email}`, { withCredentials: true });
          if (response.status === 200) {
              findReviewedSubmissions(response.data.filter(obj => obj.upload_type === 'dataset'));
          }
        } catch (error) {
          console.error('Error fetching submissions:', error.response ? error.response.data : error.message);
        }
    }, [serverHost, email]);

    const allowedSubmission = useCallback(async () =>{
        const response = await axios.get(`${serverHost}/upload/${email}/dataset/allowed`, {withCredentials: true});
        if (response.status === 200) {
            setAllowed(response.data.allowed);
        }
    }, [serverHost, email]);

    const findReviewedSubmissions = (data) => {
        for(let i = 0; i < data.length; i++) {
          if(!data[i].hasOwnProperty('approval_status')) {
            setBannerShow(true);
            setFileName(data[i].upload_key.split('/').reverse()[0]);
            setUploadType("Dataset");
          } 
        }
    }
    
    const receiveDataset = (files) => {
        onFileChange(files[0]);
    }

    const onFileChange = (file) => {
        if(file == undefined) {
            toast.error("Error: select only ONE file", toastOptions);
            return;
        }
        if (!file) {
            toast.error(`Error: unable to upload file "${file.name}"`, toastOptions);
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(`Error: file "${file.name}" is larger than maximum allowed size`, toastOptions);
            return;
        }
        const validFileNameRegex = /^[a-zA-Z0-9._-]+$/;
        if (!validFileNameRegex.test(file.name)) {
            toast.error(`Error: file name "${file.name}" has invalid characters, can only contain letters, numbers, . _ or -`, toastOptions);
            return;
        }
        const fileNameExists = Object.values(files).some(f => f && f.name === file.name);
        if (fileNameExists) {
            toast.error(`Error: uploaded file name "${file.name}" must be unique, existing file ${file.name} already exists`, toastOptions);
            return;
        }
        setFiles(prevFile => ({...prevFile, datasetZip: file}));
    };

    const resumable = new Resumable({
        target: `${serverHost}/upload`,
        query: { uploadType: "dataset" }, // we will also add submission timestamp when submitted
        fileType: ['zip'],
        maxFileSize: MAX_FILE_SIZE_BYTES,
        testChunks: false,
        chunkSize: MAX_CHUNK_SIZE_BYTES,
        withCredentials: true
    });

    resumable.on('fileAdded', function () {
        resumable.upload();
    });

    resumable.on('fileProgress', function (file) {
        if (!file) {
            return;
        }
        // Update individual file progress and success progress
        const progress = Math.floor(file.progress() * 100);
        setUploadProgress(progress);
    });

    resumable.on('fileSuccess', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        if (resumable.files.every((file) => file.isComplete())) {
            setIsUploading(false);
            setIsSubmitted(true);
            isSubmit(false);
            navigate(isAdmin ? "/admin": "/home", {state: {formSubmitted: true}});
        }
    });

    resumable.on('fileError', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        setIsUploading(false);
        setIsSubmitEnabled(true);
        isSubmit(false);
        toast.error("Error, submission not complete, file error, please try again", toastOptions);
    });

    const onSubmit = () => {
        isSubmit(true);
        toast.dismiss();
        setIsSubmitEnabled(false);
        setIsUploading(true);
        resumable.opts.query.submissionTimestamp = new Date().toISOString(); // add timestamp to query
        Object.values(files).forEach((file, index) => {
        resumable.addFile(file);
        if (!resumable.files[index]) {
            resumable.cancel();
            setIsSubmitEnabled(true);
            setIsUploading(false);
            toast.dismiss();
            toast.error("Error, submission not complete, invaild file, please try again", toastOptions);
            return;
        }
        });
    };

    useEffect(() => {
        const areAllFilesSelected = Object.keys(files).every(key => files[key] !== null);
        fetchSubmissions();
        allowedSubmission();
        setIsSubmitEnabled(!areAllFilesSelected || !allowed);
    }, [fetchSubmissions, files, allowed, allowedSubmission]);

    return(
        <Box className='container-box'>
            <Box className='banner-notice' sx={{display: bannerShow ? 'flex' : 'none'}}>
                <Typography sx={{fontSize: '20px', paddingLeft: '30px'}} color="fontTheme">
                    NOTICE
                </Typography>
                <Typography color="fontTheme" sx={{fontSize: '12px', padding: '18px'}}>
                    YOUR {uploadType.toUpperCase()} SUBMISSION, "{fileName.toUpperCase()}" IS CURRENTLY UNDER REVIEW.  YOU WILL RECEIVE A SYSTEM NOTIFICATION WHEN ITS STATUS CHANGES. 
                    YOU CAN ALSO CHECK THE SUMMARY STATUS TABLE FOR UPDATES. PLEASE BE AWARE THAT YOU CANNOT SUBMIT ANOTHER {uploadType.toUpperCase()} WHILE {fileName.toUpperCase()} IS BEING REVIEWED.
                </Typography>
            </Box>
            <Box sx={{marginTop: bannerShow ? '12px' : '92px'}}>
                <Typography color="fontTheme" 
                            className='page-header'>Dataset Submission</Typography>
                <Typography className='page-description'>
                    You can upload your dataset as a zip archive file.  Please ensure your dataset follows the MdTF format.  Upload times may vary
                    (up to ~10 minutes) depending on your internet speed and the size of your dataset zip archive.
                </Typography>
                <Typography className='page-description' sx={{paddingTop: '16px'}}>
                    <b>Important:</b> Do not navigate away from this page during upload process.
                </Typography>
            </Box>
            <Box className='below-content'>
                {(!isSubmitted && !isUploading) &&
                <Box>
                    <Typography className='page-instruction'>
                        <b>Step 1:</b> Upload zip archive file
                    </Typography>
                    <form onSubmit={onSubmit}>
                        <ZipFileUpload fileUpload={receiveDataset} bannerShow={bannerShow}/>
                    </form>
                    <Typography className='file-name'>
                        {files.datasetZip !== null 
                        ? <> <b>{files.datasetZip.path.replace('./', '')} ({byteConversion(files.datasetZip.size)})</b> </> 
                        : ''}
                    </Typography>                    
                </Box>
                }
                {isUploading &&
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <Box sx={{width: '75%', marginRight: '10px'}}>
                        <LinearProgress variant="determinate" 
                                        value={uploadProgress}
                                        color="fontTheme"
                                        sx={{height: '30px'}}/>
                    </Box>
                    <Box>
                        <Typography color='fontTheme' variant='h6'>
                            {uploadProgress}%
                        </Typography>
                    </Box>
                </Box>
                }
            </Box>
            <Grid2 container >
                <Grid2 size={{xs: 12, lg: 12}} sx={{marginTop: '100px'}}>
                    <Box sx={{float: 'left'}}>
                        <Typography className='page-description'>
                            Select <b>SUBMIT</b> to upload your file.  Submission status will be communicated to your registered email address.
                        </Typography>
                    </Box>
                </Grid2>
                <Grid2 size={{xs: 12, lg: 12}}>
                    <Box sx={{float: 'right'}}>
                        <Button className='submit-buttons' onClick={onSubmit} disabled={isSubmitEnabled} variant='ouline'>SUBMIT</Button>
                    </Box>
                </Grid2>
            </Grid2>
            <ToastContainer />
        </Box>
    )
}
